import { setTimeout as delay } from "node:timers/promises";
import type { ChannelAdapter, ChannelRuntime, OutboundMessage } from "@mewbleh/nori-plugin-sdk";
import WebSocket from "ws";

type DiscordEvent = { op: number; d?: any; s?: number | null; t?: string };

export class DiscordChannel implements ChannelAdapter {
  readonly id = "discord";
  readonly capabilities = { media: true, reactions: true, threads: true, groups: true, login: "token" as const };

  async start(runtime: ChannelRuntime): Promise<{ stop(): Promise<void> }> {
    const token = String(runtime.config.token ?? "");
    if (!token) throw new Error("Discord bot token is required");
    const socket = new WebSocket("wss://gateway.discord.gg/?v=10&encoding=json");
    let sequence: number | null = null;
    let heartbeat: NodeJS.Timeout | undefined;
    let botUserId = "";
    socket.on("message", (raw) => {
      void (async () => {
        const event = JSON.parse(raw.toString()) as DiscordEvent;
        if (typeof event.s === "number") sequence = event.s;
        if (event.op === 10) {
          const interval = Number(event.d.heartbeat_interval);
          heartbeat = setInterval(() => socket.send(JSON.stringify({ op: 1, d: sequence })), interval);
          socket.send(
            JSON.stringify({
              op: 2,
              d: {
                token,
                intents: (1 << 0) | (1 << 9) | (1 << 12) | (1 << 15),
                properties: { os: process.platform, browser: "nori", device: "nori" },
              },
            }),
          );
          return;
        }
        if (event.t === "READY") {
          botUserId = String(event.d.user.id);
          runtime.log("info", "Discord channel connected", { username: event.d.user.username });
          return;
        }
        if (event.t !== "MESSAGE_CREATE" || !event.d || event.d.author?.id === botUserId) return;
        const isDm = !event.d.guild_id;
        await runtime.onMessage({
          id: String(event.d.id),
          conversation: {
            channel: this.id,
            accountId: runtime.accountId,
            id: String(event.d.channel_id),
            kind: event.d.thread ? "thread" : isDm ? "dm" : "group",
            ...(event.d.thread?.id ? { threadId: String(event.d.thread.id) } : {}),
          },
          sender: {
            id: String(event.d.author.id),
            ...(event.d.author.global_name || event.d.author.username
              ? { displayName: String(event.d.author.global_name ?? event.d.author.username) }
              : {}),
            isBot: Boolean(event.d.author.bot),
          },
          text: String(event.d.content ?? ""),
          ...(event.d.attachments?.length
            ? {
                media: event.d.attachments.map((attachment: any) => ({
                  url: String(attachment.url),
                  mediaType: String(attachment.content_type ?? "application/octet-stream"),
                  name: String(attachment.filename),
                })),
              }
            : {}),
          ...(event.d.message_reference?.message_id ? { replyToId: String(event.d.message_reference.message_id) } : {}),
          mentioned: event.d.mentions?.some((user: any) => String(user.id) === botUserId) ?? false,
          receivedAt: Date.parse(String(event.d.timestamp)),
        });
      })().catch((error) => runtime.log("error", "Discord event failed", { message: (error as Error).message }));
    });
    socket.on("error", (error) => runtime.log("error", "Discord socket failed", { message: error.message }));
    const abort = () => socket.close(1000, "Nori stopping");
    runtime.signal.addEventListener("abort", abort, { once: true });
    await Promise.race([
      new Promise<void>((resolve, reject) => {
        socket.once("open", resolve);
        socket.once("error", reject);
      }),
      delay(15_000, undefined, { signal: runtime.signal }).then(() => Promise.reject(new Error("Discord connection timed out"))),
    ]);
    return {
      stop: async () => {
        runtime.signal.removeEventListener("abort", abort);
        if (heartbeat) clearInterval(heartbeat);
        socket.close(1000, "Nori stopping");
      },
    };
  }

  async send(message: OutboundMessage, runtime: Omit<ChannelRuntime, "onMessage">): Promise<{ messageId: string }> {
    const token = String(runtime.config.token ?? "");
    const attachment = message.media?.find((item) => item.data);
    let requestBody: BodyInit;
    let headers: Record<string, string>;
    if (attachment?.data) {
      const form = new FormData();
      form.append("payload_json", JSON.stringify({
        content: message.text,
        ...(message.replyToId ? { message_reference: { message_id: message.replyToId, fail_if_not_exists: false } } : {}),
      }));
      form.append("files[0]", new Blob([Uint8Array.from(attachment.data)], { type: attachment.mediaType }), attachment.name ?? "attachment");
      requestBody = form;
      headers = { authorization: `Bot ${token}` };
    } else {
      requestBody = JSON.stringify({
        content: [message.text, ...((message.media ?? []).flatMap((item) => item.url ? [item.url] : []))].filter(Boolean).join("\n"),
        ...(message.replyToId ? { message_reference: { message_id: message.replyToId, fail_if_not_exists: false } } : {}),
      });
      headers = { authorization: `Bot ${token}`, "content-type": "application/json" };
    }
    const response = await fetch(`https://discord.com/api/v10/channels/${message.conversation.id}/messages`, {
      method: "POST",
      headers,
      body: requestBody,
      signal: runtime.signal,
    });
    if (!response.ok) throw new Error(`Discord send failed (${response.status}): ${(await response.text()).slice(0, 300)}`);
    const responseBody = (await response.json()) as { id: string };
    return { messageId: responseBody.id };
  }

  async probe(config: Record<string, unknown>): Promise<{ ok: boolean; message?: string }> {
    try {
      const response = await fetch("https://discord.com/api/v10/users/@me", {
        headers: { authorization: `Bot ${String(config.token ?? "")}` },
      });
      if (!response.ok) throw new Error(`Discord probe failed (${response.status})`);
      const user = (await response.json()) as { username: string };
      return { ok: true, message: user.username };
    } catch (error) {
      return { ok: false, message: (error as Error).message };
    }
  }
}
